import {KeyboardSensor, PointerSensor} from '@dnd-kit/dom';

import {ROW_TYPE} from 'src/types/byod';

/**
 * Drag-and-drop type tags applied to every sortable / droppable.
 * `@dnd-kit` matches `accept` against `type`; tagging KPI tiles separately
 * from chart/table tiles is what enforces "only KPIs land in KPI rows."
 */
export const DRAG_TYPE = {
  KPI: 'kpi-tiles',
  VIZ: 'viz-tiles',
} as const;

export type DragType = typeof DRAG_TYPE[keyof typeof DRAG_TYPE];

/**
 * Map a row's `type` (KPI / VIZ / SECTION) to the drop-tag tiles in that row
 * carry. Section rows aren't drop targets — callers should branch them out
 * before reaching this helper.
 */
export function dropTypeForRow(rowType: string): DragType {
  return rowType === ROW_TYPE.KPI ? DRAG_TYPE.KPI : DRAG_TYPE.VIZ;
}

/**
 * Sensors for the BYOD canvas's `<DragDropProvider>`.
 *
 * The default `KeyboardSensor` from `@dnd-kit/dom` moves the source by a flat
 * 10px coordinate delta per arrow press, which is too coarse for tile widths
 * of 150–500px and lets repeated `→` presses walk the source past row edges
 * into a different row. We disable the default arrow handling here (empty
 * arrays for up/down/left/right) and let `useByodKeyboardNav` install its
 * own row-aware handler that snaps to discrete drop targets instead.
 *
 * Space / Enter still start a keyboard drag; Space / Enter / Tab end one;
 * Escape cancels.
 */
export const BYOD_SENSORS = [
  PointerSensor,
  KeyboardSensor.configure({
    keyboardCodes: {
      start: ['Space', 'Enter'],
      cancel: ['Escape'],
      end: ['Space', 'Enter', 'Tab'],
      up: [],
      down: [],
      left: [],
      right: [],
    },
  }),
];
